<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Automations</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Email Campaigns</a></li>
                    </ul>

                    <h3>Manage Email Campaigns</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewEmailCampaign" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Email Campaign</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="emailCampaigns"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No email campaigns found.
                        </template>

                        <template #loading>
                            Loading email campaigns. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Initiated</span>
                                    <span v-if="data.status === 'S'"
                                          :class="'status-badge status-' + data.status">Started</span>
                                    <span v-if="data.status === 'P'"
                                          :class="'status-badge status-' + data.status">Processing</span>
                                    <span v-if="data.status === 'C'"
                                          :class="'status-badge status-' + data.status">Completed</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Email Campaign {{ this.emailCampaign.name }}</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="emailCampaign.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="emailSubject">Email Subject</label>
                        <InputText id="emailSubject" v-model.trim="emailCampaign.emailSubject" required="true"/>
                    </div>

                    <div class="field">
                        <label for="emailPreview">Email Preview</label>
                        <Textarea v-model.trim="emailCampaign.emailPreview"
                                  :autoResize="true" rows="3" cols="50"/>
                    </div>

                    <div class="field">
                        <label for="finalNoticeEmailTemplate">Email Content</label>
                        <Editor v-model="emailCampaign.emailContent" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                    <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                    <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                    <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                    <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                    <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                    <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                    <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="finalNoticeEmailTemplate">Email Footer</label>
                        <Editor v-model="emailCampaign.emailFooter" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                    <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                    <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                    <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                    <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                    <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                    <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                    <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="logo">Logo</label>
                        <Editor v-model="emailCampaign.logo" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-image"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="logo">Image #1</label>
                        <Editor v-model="emailCampaign.image1" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-image"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :disabled="!canUpdateEmailCampaign" :loading="submitted"
                            @click="updateEmailCampaign"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Email Campaign {{ this.emailCampaign.name }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Delete this Email Campaign?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted" @click="deleteEmailCampaign"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Email Campaign</h3>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newEmailCampaign.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="emailSubject">Email Subject</label>
                        <InputText id="emailSubject" v-model.trim="newEmailCampaign.emailSubject" required="true"/>
                    </div>

                    <div class="field">
                        <label for="emailPreview">Email Preview</label>
                        <Textarea v-model.trim="newEmailCampaign.emailPreview"
                                  :autoResize="true" rows="3" cols="50"/>
                    </div>

                    <div class="field">
                        <label for="finalNoticeEmailTemplate">Email Content</label>
                        <Editor v-model="newEmailCampaign.emailContent" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                    <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                    <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                    <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                    <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                    <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                    <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                    <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="finalNoticeEmailTemplate">Email Footer</label>
                        <Editor v-model="newEmailCampaign.emailFooter" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                    <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                    <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                    <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                    <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                    <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                    <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                    <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                    <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="logo">Logo</label>
                        <Editor v-model="newEmailCampaign.logo" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-image"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="logo">Image #1</label>
                        <Editor v-model="newEmailCampaign.image1" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-image"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="recipientType">Recipient Type</label>
                        <Dropdown v-model="newEmailCampaign.recipientType" :options="receipientTypes"
                                  optionLabel="name" optionValue="code" placeholder="Select a Type"/>
                    </div>

                    <div v-if="newEmailCampaign.recipientType === 'L'" class="field">
                        <label for="recipientLabel">Label</label>
                        <MultiSelect
                            v-model="newEmailCampaign.labels"
                            :options="labels"
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select Labels"
                            display="chip"
                            :filter="true"/>

                    </div>

                    <div v-if="newEmailCampaign.recipientType === 'S'" class="field">
                        <label for="recipientClientSegment">Client Segment</label>
                        <MultiSelect
                            v-model="newEmailCampaign.clientSegments"
                            :options="clientSegments"
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select Client Segments"
                            display="chip"
                            :filter="true"/>

                    </div>

                    <Button label="Create" class="mr-2 mb-2" :disabled="!canCreateEmailCampaign" :loading="submitted"
                            @click="createEmailCampaign"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Start Email Campaign {{ this.emailCampaign.name }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Start this Email Campaign?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Start" class="mr-2 mb-2" :loading="submitted" @click="startEmailCampaign"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Clients for Email Campaign {{ this.emailCampaign.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Back to Manage EmailCampaigns</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="clientEmailCampaigns"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'description']">

                        <template #empty>
                            No clients found for email campaign.
                        </template>

                        <template #loading>
                            Loading clients for email campaign. Please wait.
                        </template>

                        <Column field="clientName" header="Client Name" :sortable="true"/>
                        <Column field="created" header="Created" :sortable="true"/>

                    </DataTable>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import EmailCampaignService from "@/service/EmailCampaignService";
import LabelService from "@/service/LabelService";
import ClientSegmentService from "@/service/ClientSegmentService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'name', header: 'Name', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'id', header: 'ID', sortable: true},
            {field: 'name', header: 'Name', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            labels: [],
            clientSegments: [],
            emailCampaign: {},
            emailCampaigns: null,
            newEmailCampaign: {},
            originalEmailCampaigns: null,
            confirmationResponse: 'N',
            startEmailCampaignResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            clientEmailCampaigns: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypesInitiated: [
                {name: 'Started', code: 'S'}
            ],
            statusTypesStarted: [
                {name: 'Initiated', code: 'I'},
                {name: 'Started', code: 'S'}
            ],
            receipientTypes: [
                {name: 'Clients (Active)', code: 'C01'},
                {name: 'Clients (Inactive)', code: 'C02'},
                {name: 'Clients (Active and Inactive)', code: 'C03'},
                {name: 'Prospects (Won)', code: 'P01'},
                {name: 'Prospects (Lost)', code: 'P02'},
                {name: 'Prospects (Initiated)', code: 'P03'},
                {name: 'Label', code: 'L'},
                {name: 'Client Segment', code: 'S'},
            ]
        }
    },
    watch: {
        emailCampaigns() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    computed: {
        canCreateEmailCampaign() {
            if ((this.newEmailCampaign.name) && (this.newEmailCampaign.emailSubject)
                && (this.newEmailCampaign.emailPreview) && (this.newEmailCampaign.emailContent)
                && (this.newEmailCampaign.emailFooter) && (this.newEmailCampaign.recipientType)) {
                return true;
            } else {
                return false;
            }
        },
        canUpdateEmailCampaign() {
            if (this.emailCampaign.status !== 'I') {
                return false;
            } else if ((this.emailCampaign.name) && (this.emailCampaign.emailSubject)
                && (this.emailCampaign.emailPreview) && (this.emailCampaign.emailContent)
                && (this.emailCampaign.emailFooter) && (this.emailCampaign.status)) {
                return true;
            } else {
                return false;
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigurationData, companyConfigurationData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                await this.loadData();

                this.loading = false;

            } catch (e) {
                console.error("[EmailCampaigns][mounted] Error fetching additional data = ", e);
            }

            console.log("[EmailCampaigns][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[EmailCampaigns][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        async loadData() {
            this.submitted = true;
            try {
                const [emailCampaignData] = await Promise.all([
                    EmailCampaignService.getCompanyEmailCampaigns(this.profile.companyId)
                ]);

                this.emailCampaigns = emailCampaignData.data;
                this.originalEmailCampaigns = [...this.emailCampaigns];
                console.log("[EmailCampaigns][loadData] emailCampaignData.data = ", emailCampaignData.data);
            } catch (error) {
                console.error("[EmailCampaigns][loadData] Error loading email campaigns:", error);
                this.emailCampaigns = [];
                this.originalEmailCampaigns = [];
            } finally {
                this.submitted = false;
            }
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        async restoreToOriginal() {
            await this.loadData();
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        async callNewEmailCampaign() {
            this.activeTab = 4;
            this.newEmailCampaign = {};

            this.submitted = true;
            try {
                const [labelData, clientSegmentData] = await Promise.all([
                    LabelService.getCompanyLabels(this.profile.companyId),
                    ClientSegmentService.getCompanyClientSegments(this.profile.companyId)
                ]);

                this.labels = [...labelData.data];
                this.clientSegments = [...clientSegmentData.data];

                console.log("[EmailCampaigns][callNewEmailCampaign] this.labels = ", this.labels);
                console.log("[EmailCampaigns][callNewEmailCampaign] this.clientSegments = ", this.clientSegments);
            } catch (error) {
                console.error("[EmailCampaigns][callNewEmailCampaign] Error loading labels:", error);
                this.labels = [];
                this.clientSegments = [];
            } finally {
                this.submitted = false;
            }
        },
        callUpdateEmailCampaign(data) {
            this.activeTab = 2;
            this.emailCampaign = data;
            this.startEmailCampaignResponse = 'N';
        },
        callDeleteEmailCampaign(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.emailCampaign = data;
        },
        callStartEmailCampaign(data) {
            this.activeTab = 5;
            this.confirmationResponse = 'N';
            this.emailCampaign = data;
        },
        async callViewRecipients(data) {
            // this.activeTab = 6;
            this.emailCampaign = data;

            // const [clientEmailCampaignData] = await Promise.all([
            //     ClientEmailCampaignService.getClientsForEmailCampaign(this.emailCampaign.id)
            // ]);
            //
            // this.clientEmailCampaigns = clientEmailCampaignData.data;
            //
            // console.log('[EmailCampaigns][callViewClients] this.clientEmailCampaigns = ', this.clientEmailCampaigns);
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.emailCampaigns !== null) {

                if (isActive) {
                    this.emailCampaigns = this.originalEmailCampaigns.filter(label => label.status === 'A');
                } else if (isInactive) {
                    this.emailCampaigns = this.originalEmailCampaigns.filter(label => label.status === 'I');
                } else {
                    this.emailCampaigns = this.originalEmailCampaigns;
                }

            } else {
                this.emailCampaigns = [];
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            if (data.status === 'I') {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdateEmailCampaign(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteEmailCampaign(data);
                    }
                });

                items.push({
                    label: 'Start',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callStartEmailCampaign(data);
                    }
                });
            }

            items.push({
                label: 'View Recipients',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewRecipients(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        async updateEmailCampaign() {
            this.submitted = true;

            if (this.emailCampaign.id) {

                try {
                    var emailCampaignDto = {
                        name: this.emailCampaign.name,
                        emailSubject: this.emailCampaign.emailSubject,
                        emailPreview: this.emailCampaign.emailPreview,
                        emailContent: this.emailCampaign.emailContent,
                        logo: this.emailCampaign.logo,
                        image1: this.emailCampaign.image1,
                        emailFooter: this.emailCampaign.emailFooter
                    };

                    const response = await EmailCampaignService.updateEmailCampaign(this.emailCampaign.id, emailCampaignDto);
                    console.log('[EmailCampaigns][updateEmailCampaign] Response = ', response);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated email campaign',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[EmailCampaigns][updateEmailCampaign] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update email campaign. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async startEmailCampaign() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.emailCampaign.id) {

                    try {
                        const response = await EmailCampaignService.startEmailCampaign(this.emailCampaign.id);
                        console.log('[EmailCampaigns][startEmailCampaign] Response = ', response);

                        await this.loadData();

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Started email campaign',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[EmailCampaigns][startEmailCampaign] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to start email campaign. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteEmailCampaign() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.emailCampaign.id) {

                    try {
                        const response = await EmailCampaignService.deleteEmailCampaign(this.emailCampaign.id);
                        console.log('[EmailCampaigns][deleteEmailCampaign] Response = ', response);

                        await this.loadData();

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted email campaign',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[EmailCampaigns][deleteEmailCampaign] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete email campaign. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createEmailCampaign() {
            this.submitted = true;

            if (this.newEmailCampaign.name) {

                console.log('[EmailCampaigns][createEmailCampaign] this.newEmailCampaign.recipientType = ', this.newEmailCampaign.recipientType);
                console.log('[EmailCampaigns][createEmailCampaign] this.newEmailCampaign.labels = ', this.newEmailCampaign.labels);
                console.log('[EmailCampaigns][createEmailCampaign] this.newEmailCampaign.clientSegments = ', this.newEmailCampaign.clientSegments);

                try {
                    var newEmailCampaignDto = {
                        name: this.newEmailCampaign.name,
                        emailSubject: this.newEmailCampaign.emailSubject,
                        emailPreview: this.newEmailCampaign.emailPreview,
                        emailContent: this.newEmailCampaign.emailContent,
                        logo: this.newEmailCampaign.logo,
                        image1: this.newEmailCampaign.image1,
                        emailFooter: this.newEmailCampaign.emailFooter,
                        recipientType: this.newEmailCampaign.recipientType,
                        labels: this.newEmailCampaign.labels,
                        clientSegments: this.newEmailCampaign.clientSegments,
                        status: 'I'
                    };

                    const response =
                        await EmailCampaignService.createEmailCampaign(this.profile.companyId, newEmailCampaignDto);
                    console.log('[EmailCampaigns][createEmailCampaign] Response = ', response);

                    await this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created email campaign',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[EmailCampaigns][createEmailCampaign] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create email campaign. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-C {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-P {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-S {
        background: #FFCDD2;
        color: #C63737;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
